import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Typography,
  Chip,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import {
  getPreservicioById,
  getNotasPreservicio,
  addNotaPreservicio,
  addDetallePreservicio,
  deleteDetallePreservicio,
  updatePreservicio,
  convertirPreservicio,
  getProductosParaPreservicio,
} from '../../api/preservicios';
import moment from 'moment';
import { useAuth } from '../../context/auth';
import { UseAlert } from '../../components/newAlert/NewAlert';

const ESTADOS: Record<number, { label: string; color: string }> = {
  1: { label: 'Pendiente', color: '#f5a623' },
  2: { label: 'En revisión', color: '#4a90d9' },
  3: { label: 'Servicio Asignado', color: '#28a745' },
  4: { label: 'Rechazado/Cancelado', color: '#d9534f' },
};

const ViewPreservicio = ({ id, onClose }: { id: number; onClose: () => void }) => {
  const { permisos, user } = useAuth();
  const { Prm_Preservicio_Gestionar, Prm_Preservicio_Convertir } = permisos;
  const esCliente = !!user?.Us_Cl_Id;

  const [loading, setLoading] = useState(true);
  const [preservicio, setPreservicio] = useState<any>(null);
  const [detalle, setDetalle] = useState<any[]>([]);
  const [notas, setNotas] = useState<any[]>([]);
  const [productos, setProductos] = useState<any[]>([]);
  const [nuevaNota, setNuevaNota] = useState('');
  const [productoNuevo, setProductoNuevo] = useState<number | ''>('');
  const [cantidadNueva, setCantidadNueva] = useState(1);
  const [convirtiendo, setConvirtiendo] = useState(false);
  const [preciosConversion, setPreciosConversion] = useState<Record<number, { valorUnitario: string; iva: string }>>({});
  const [rechazando, setRechazando] = useState(false);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const { NewNotification } = UseAlert();

  const cargar = useCallback(async () => {
    setLoading(true);
    const [resPreservicio, resNotas] = await Promise.all([getPreservicioById(id), getNotasPreservicio(id)]);
    if (resPreservicio.isOk) {
      setPreservicio(resPreservicio.data.preservicio);
      setDetalle(resPreservicio.data.detalle);

      // El catalogo depende del cliente dueño del preservicio, por eso se pide despues de
      // conocerlo: el personal interno debe mandar ese Cl_Id (un cliente lo resuelve el
      // backend con su propio token e ignora el parametro).
      const resProductos = await getProductosParaPreservicio(resPreservicio.data.preservicio?.Psrv_Cl_Id);
      if (resProductos.isOk) {
        setProductos(resProductos.data);
      }
    }
    if (resNotas.isOk) {
      setNotas(resNotas.data);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  if (loading || !preservicio) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  const esDueño = esCliente && preservicio.Psrv_Cl_Id === user?.Us_Cl_Id;
  const puedeGestionar = !esCliente && Prm_Preservicio_Gestionar === 1;
  const puedeConvertir = !esCliente && Prm_Preservicio_Convertir === 1;
  const esPendiente = preservicio.Psrv_Estado === 1;
  const esConvertible = preservicio.Psrv_Estado === 1 || preservicio.Psrv_Estado === 2;
  const esConvertido = preservicio.Psrv_Estado === 3;
  const puedeEditarDetalle = (esDueño && esPendiente) || puedeGestionar;
  const vehiculoCompleto = !!(preservicio.Psrv_VehiculoMarca && preservicio.Psrv_VehiculoReferencia && preservicio.Psrv_VehiculoPlacaOSerial);

  // Actualiza un solo campo (Marca/Referencia/Placa/OrdenServicio/los 3 checks) y, si
  // guarda bien, refleja el cambio de inmediato en el estado local -- antes los checkboxes
  // quedaban "pegados" porque su `checked` seguia leyendo el `preservicio` viejo hasta
  // cerrar y reabrir el dialogo, y no habia ninguna notificacion de exito/error.
  const handleUpdateCampo = async (campo: string, valor: any) => {
    const response = await updatePreservicio(id, { [campo]: valor });
    if (response.isOk) {
      setPreservicio((prev: any) => ({ ...prev, [campo]: valor }));
      NewNotification('Actualizado exitosamente', 'success');
    } else {
      NewNotification(response.message || 'Error al actualizar', 'error');
    }
  };

  const handleAgregarProducto = async () => {
    if (!productoNuevo || cantidadNueva <= 0) return;
    const response = await addDetallePreservicio(id, productoNuevo as number, cantidadNueva);
    if (response.isOk) {
      NewNotification('Producto agregado', 'success');
      setProductoNuevo('');
      setCantidadNueva(1);
      cargar();
    } else {
      NewNotification(response.message || 'Error al agregar el producto', 'error');
    }
  };

  const handleEliminarProducto = async (detalleId: number) => {
    const response = await deleteDetallePreservicio(detalleId);
    if (response.isOk) {
      cargar();
    } else {
      NewNotification(response.message || 'Error al eliminar el producto', 'error');
    }
  };

  const handleAgregarNota = async () => {
    if (!nuevaNota.trim()) return;
    const response = await addNotaPreservicio(id, nuevaNota);
    if (response.isOk) {
      setNuevaNota('');
      cargar();
    } else {
      NewNotification(response.message || 'Error al agregar la nota', 'error');
    }
  };

  const handleCancelar = async () => {
    const response = await updatePreservicio(id, { Psrv_Estado: 4 });
    if (response.isOk) {
      NewNotification('Preservicio cancelado', 'success');
      onClose();
    } else {
      NewNotification(response.message || 'Error al cancelar', 'error');
    }
  };

  const handleMarcarEnRevision = async () => {
    const response = await updatePreservicio(id, { Psrv_Estado: 2 });
    if (response.isOk) {
      cargar();
    } else {
      NewNotification(response.message || 'Error', 'error');
    }
  };

  const handleRechazar = async () => {
    if (!motivoRechazo.trim()) {
      NewNotification('Indica el motivo del rechazo -- se le notifica al cliente', 'warning');
      return;
    }
    const response = await updatePreservicio(id, { Psrv_Estado: 4, MotivoRechazo: motivoRechazo });
    if (response.isOk) {
      NewNotification('Preservicio rechazado', 'success');
      onClose();
    } else {
      NewNotification(response.message || 'Error al rechazar', 'error');
    }
  };

  const handleConvertir = async () => {
    const detalleConvertir = detalle.map((d) => ({
      PsrvD_Id: d.PsrvD_Id,
      ValorUnitario: parseFloat(preciosConversion[d.PsrvD_Id]?.valorUnitario || '0'),
      Iva: parseFloat(preciosConversion[d.PsrvD_Id]?.iva || '0'),
    }));

    if (detalleConvertir.some((d) => !d.ValorUnitario || d.ValorUnitario <= 0)) {
      NewNotification('Debe indicar el valor unitario de todos los productos', 'warning');
      return;
    }

    const response = await convertirPreservicio(id, { Detalle: detalleConvertir });
    if (response.isOk) {
      NewNotification('Preservicio convertido exitosamente', 'success');
      onClose();
    } else {
      NewNotification(response.message || 'Error al convertir', 'error');
    }
  };

  return (
    <Box sx={{ marginTop: 1 }}>
      <Box display='flex' justifyContent='space-between' alignItems='center' sx={{ marginBottom: 2 }}>
        <Chip label={ESTADOS[preservicio.Psrv_Estado]?.label ?? 'Desconocido'} sx={{ backgroundColor: ESTADOS[preservicio.Psrv_Estado]?.color, color: 'white' }} />
        {!esCliente && <Typography variant='body2'>Cliente: {preservicio.Cl_Nombre}</Typography>}
      </Box>

      <Box display='flex' gap={2} sx={{ marginBottom: 2 }}>
        <TextField label='Marca' size='small' fullWidth defaultValue={preservicio.Psrv_VehiculoMarca ?? ''} disabled={!puedeGestionar || esConvertido} onBlur={(e) => puedeGestionar && !esConvertido && handleUpdateCampo('Psrv_VehiculoMarca', e.target.value)} />
        <TextField label='Referencia' size='small' fullWidth defaultValue={preservicio.Psrv_VehiculoReferencia ?? ''} disabled={!puedeGestionar || esConvertido} onBlur={(e) => puedeGestionar && !esConvertido && handleUpdateCampo('Psrv_VehiculoReferencia', e.target.value)} />
        <TextField label='Placa/Serial' size='small' fullWidth defaultValue={preservicio.Psrv_VehiculoPlacaOSerial ?? ''} disabled={!puedeGestionar || esConvertido} onBlur={(e) => puedeGestionar && !esConvertido && handleUpdateCampo('Psrv_VehiculoPlacaOSerial', e.target.value)} />
      </Box>
      {!vehiculoCompleto && <Typography variant='caption' color='warning.main'>Faltan datos del vehículo -- se deben completar antes de convertir a Servicio.</Typography>}

      <TextField
        label='Orden de Servicio'
        size='small'
        fullWidth
        defaultValue={preservicio.Psrv_OrdenServicio ?? ''}
        disabled={!puedeGestionar && !esDueño}
        helperText='Se puede editar incluso despues de convertido -- queda reflejada en las Observaciones del Servicio'
        onBlur={(e) => (puedeGestionar || esDueño) && handleUpdateCampo('Psrv_OrdenServicio', e.target.value)}
        sx={{ marginTop: 2 }}
      />

      <FormGroup row sx={{ marginTop: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!preservicio.Psrv_CarroDisponible}
              disabled={!puedeGestionar && !esDueño}
              onChange={(e) => handleUpdateCampo('Psrv_CarroDisponible', e.target.checked)}
            />
          }
          label='Carro disponible'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={!!preservicio.Psrv_CargoCliente}
              disabled={!puedeGestionar && !esDueño}
              onChange={(e) => handleUpdateCampo('Psrv_CargoCliente', e.target.checked)}
            />
          }
          label='Cargo Cliente'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={!!preservicio.Psrv_CargoComercial}
              disabled={!puedeGestionar && !esDueño}
              onChange={(e) => handleUpdateCampo('Psrv_CargoComercial', e.target.checked)}
            />
          }
          label='Cargo Comercial'
        />
      </FormGroup>

      <Typography variant='subtitle2' sx={{ marginTop: 2, marginBottom: 1 }}>Productos</Typography>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Producto</TableCell>
            <TableCell>Cantidad</TableCell>
            {convirtiendo && <TableCell>Valor unitario</TableCell>}
            {convirtiendo && <TableCell>IVA</TableCell>}
            {puedeEditarDetalle && !convirtiendo && <TableCell>Eliminar</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {detalle.map((d) => (
            <TableRow key={d.PsrvD_Id}>
              <TableCell>
                {d.Pr_Nombre} {d.Un_Nombre}
              </TableCell>
              <TableCell>{d.PsrvD_Cantidad}</TableCell>
              {convirtiendo && (
                <TableCell>
                  <TextField
                    type='number'
                    size='small'
                    sx={{ width: 110 }}
                    value={preciosConversion[d.PsrvD_Id]?.valorUnitario ?? ''}
                    onChange={(e) => setPreciosConversion((prev) => ({ ...prev, [d.PsrvD_Id]: { ...prev[d.PsrvD_Id], valorUnitario: e.target.value } }))}
                  />
                </TableCell>
              )}
              {convirtiendo && (
                <TableCell>
                  <TextField
                    type='number'
                    size='small'
                    sx={{ width: 90 }}
                    value={preciosConversion[d.PsrvD_Id]?.iva ?? ''}
                    onChange={(e) => setPreciosConversion((prev) => ({ ...prev, [d.PsrvD_Id]: { ...prev[d.PsrvD_Id], iva: e.target.value } }))}
                  />
                </TableCell>
              )}
              {puedeEditarDetalle && !convirtiendo && (
                <TableCell>
                  <IconButton size='small' onClick={() => handleEliminarProducto(d.PsrvD_Id)}>
                    <Delete color='error' fontSize='small' />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {puedeEditarDetalle && !convirtiendo && (
        <Box display='flex' gap={1} alignItems='center' sx={{ marginTop: 1 }}>
          <FormControl size='small' fullWidth>
            <InputLabel>Producto</InputLabel>
            <Select value={productoNuevo} onChange={(e) => setProductoNuevo(e.target.value as number)} label='Producto'>
              {productos.map((p) => (
                <MenuItem key={p.Pr_Id} value={p.Pr_Id}>
                  {p.Pr_Nombre} {p.Un_Nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label='Cant.' type='number' size='small' sx={{ width: 90 }} value={cantidadNueva} onChange={(e) => setCantidadNueva(parseInt(e.target.value, 10) || 1)} />
          <IconButton onClick={handleAgregarProducto}>
            <Add color='success' />
          </IconButton>
        </Box>
      )}

      <Divider sx={{ marginY: 2 }} />

      <Typography variant='subtitle2' sx={{ marginBottom: 1 }}>Notas</Typography>
      <Box sx={{ maxHeight: 180, overflowY: 'auto', marginBottom: 1 }}>
        {notas.map((n) => (
          <Box key={n.PsrvN_Id} sx={{ marginBottom: 1, padding: 1, backgroundColor: n.PsrvN_Sistema ? '#f5f5f5' : '#eef6ff', borderRadius: 1 }}>
            <Typography variant='caption' color='text.secondary'>
              {n.PsrvN_Fecha ? moment(n.PsrvN_Fecha).format('YYYY-MM-DD HH:mm') : ''} -- {n.Us_Nombre}
            </Typography>
            <Typography variant='body2'>{n.PsrvN_Texto}</Typography>
          </Box>
        ))}
        {notas.length === 0 && <Typography variant='body2' color='text.secondary'>Sin notas todavía.</Typography>}
      </Box>
      {puedeGestionar && (
        <Box display='flex' gap={1} sx={{ marginBottom: 2 }}>
          <TextField label='Agregar nota' size='small' fullWidth value={nuevaNota} onChange={(e) => setNuevaNota(e.target.value)} />
          <Button variant='outlined' size='small' onClick={handleAgregarNota}>
            Agregar
          </Button>
        </Box>
      )}

      <Box display='flex' justifyContent='space-between' gap={1} sx={{ marginTop: 2 }}>
        <Box display='flex' gap={1}>
          {esDueño && esPendiente && (
            <Button variant='outlined' color='error' size='small' onClick={handleCancelar}>
              Cancelar solicitud
            </Button>
          )}
          {puedeGestionar && esPendiente && (
            <Button variant='outlined' size='small' onClick={handleMarcarEnRevision}>
              Marcar en revisión
            </Button>
          )}
          {puedeGestionar && esConvertible && !rechazando && (
            <Button variant='outlined' color='error' size='small' onClick={() => setRechazando(true)}>
              Rechazar
            </Button>
          )}
          {rechazando && (
            <>
              <TextField
                label='Motivo del rechazo'
                size='small'
                value={motivoRechazo}
                onChange={(e) => setMotivoRechazo(e.target.value)}
                sx={{ minWidth: 220 }}
              />
              <Button variant='contained' color='error' size='small' onClick={handleRechazar}>
                Confirmar rechazo
              </Button>
              <Button variant='outlined' size='small' onClick={() => setRechazando(false)}>
                Cancelar
              </Button>
            </>
          )}
          {puedeConvertir && esConvertible && !convirtiendo && (
            <Button variant='contained' size='small' onClick={() => setConvirtiendo(true)} disabled={!vehiculoCompleto || detalle.length === 0}>
              Convertir a Servicio
            </Button>
          )}
          {convirtiendo && (
            <>
              <Button variant='contained' color='success' size='small' onClick={handleConvertir}>
                Confirmar conversión
              </Button>
              <Button variant='outlined' size='small' onClick={() => setConvirtiendo(false)}>
                Cancelar
              </Button>
            </>
          )}
        </Box>
        <Button onClick={onClose} variant='outlined' color='inherit' size='small'>
          Cerrar
        </Button>
      </Box>
    </Box>
  );
};

export default ViewPreservicio;
